/**
 * /insights — public "View All" feed combining trackers + snapshots.
 *
 * Tabbed (All / Trackers / Snapshots) with optional sector filter chips.
 * Reads /api/insights + /api/sectors.
 */
import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import PublicNav from "../layouts/PublicNav";
import SeoPageHead from "../components/SeoPageHead";
import { useLanguage } from "../hooks/useLanguage";
import { useSectorLabels } from "../lib/useSectorLabels";

export default function InsightsList() {
  const [tab, setTab] = useState("all");
  const [sector, setSector] = useState("");
  const [sectors, setSectors] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const { lang } = useLanguage();
  const { sectorLabel } = useSectorLabels();

  useEffect(() => { api.get("/sectors").then((r) => setSectors(r.data)); }, []);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (tab !== "all") params.set("kind", tab);
    if (sector) params.set("sector", sector);
    api.get(`/insights?${params.toString()}`)
      .then((r) => setItems(r.data.items || []))
      .finally(() => setLoading(false));
  }, [tab, sector]);

  const counts = useMemo(() => {
    const c = { all: items.length, trackers: 0, snapshots: 0 };
    items.forEach((it) => { c[it.kind === "tracker" ? "trackers" : "snapshots"]++; });
    return c;
  }, [items]);

  return (
    <div className="min-h-screen bg-white themable-public">
      <SeoPageHead
        pageKey="insights"
        path="/insights"
        defaultTitle="Live Insights — Pakistan Consumer Data | oodel."
        defaultDescription="Browse every live tracker and snapshot from oodel's representative Pakistan consumer panel."
      />
      <PublicNav />
      <div className="max-w-[1400px] mx-auto px-6 py-12">
        <div className="border-b border-black pb-6 mb-8">
          <div className="overline mb-2">SECTION — INSIGHTS</div>
          <h1 className="text-5xl md:text-6xl font-black tracking-tighter">All trackers & snapshots</h1>
          <p className="text-neutral-600 mt-2">Every public insight oodel. has ever published, sorted newest first.</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 border-b border-neutral-200">
          {["all", "trackers", "snapshots"].map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`insights-tab-${t}`}
                    className={`px-4 py-2 text-sm border-b-2 -mb-px capitalize ${tab === t ? "border-mango text-black font-bold" : "border-transparent text-neutral-500 hover:text-black"}`}>
              {t} {tab === t && counts[t] != null && <span className="ml-1 text-neutral-400">({counts[t]})</span>}
            </button>
          ))}
        </div>

        {/* Sector chips */}
        <div className="flex flex-wrap gap-2 mb-8" data-testid="insights-sector-chips">
          <button onClick={() => setSector("")} className={`text-xs px-3 py-1.5 border ${sector === "" ? "bg-black text-white border-black" : "border-neutral-300 hover:border-black"}`}>All sectors</button>
          {sectors.map((s) => (
            <button key={s.key} onClick={() => setSector(s.key)} data-testid={`chip-${s.key}`}
                    className={`text-xs px-3 py-1.5 border ${sector === s.key ? "bg-black text-white border-black" : "border-neutral-300 hover:border-black"}`}>
              {s.icon} {sectorLabel(s, lang)}
            </button>
          ))}
        </div>

        {loading && <div className="text-neutral-500">Loading…</div>}
        {!loading && items.length === 0 && (
          <div className="text-center py-20 border border-dashed border-neutral-300">
            <div className="text-4xl mb-2">📭</div>
            <div className="text-lg font-bold">No insights yet in this view.</div>
            <div className="text-sm text-neutral-500 mt-1">Try another tab or sector.</div>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="insights-grid">
          {items.map((it) => (
            <Link key={`${it.kind}-${it.id}`} to={it.kind === "tracker" ? `/trackers/${it.id}` : `/snapshots/${it.id}`}
                  className="group border border-neutral-200 hover:border-black bg-white p-6 block transition-all">
              <div className="flex items-center justify-between mb-2">
                <div className="overline text-neutral-500">{sectorLabel(it.sector, lang) || "—"}</div>
                <div className="overline text-mango">{it.kind === "tracker" ? "TRACKER" : "SNAPSHOT"}</div>
              </div>
              <h3 className="text-lg font-bold tracking-tight mb-3 group-hover:text-mango transition-colors">{it.name}</h3>
              <div className="text-xs text-neutral-500">n = {it.n_sample || "?"} · {it.latest_date}</div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
